import * as actionTypes from './actionTypes';
import { LanguageActionTypes } from './actionTypes';
import { HttpError } from '../../config/Axios/axios-instance';
import { Language } from '../../domain/Language';
import { ListResults } from '../../common/List/List';

export type LanguageStateType = {
  language: Language | null;
  languageSuccess: boolean;
  languageLoading: boolean;
  languageError: HttpError;
  languages: Language[];
  languagesLoading: boolean;
  languagesError: HttpError;
  languageStatusChangeLoading: boolean;
  languageStatusChangeError: HttpError;
  languageStatusChangeSuccess: boolean;
  languagesListUpdateNeeded: boolean;
  languagesList: ListResults<Language> | null;
  languagesListLoading: boolean;
  languagesListError: HttpError;
};

export type LanguageActionType = LanguageStateType & {
  type: LanguageActionTypes;
};

export const initialState: LanguageStateType = {
  language: null,
  languageSuccess: false,
  languageLoading: false,
  languageError: null,
  languages: [],
  languagesLoading: false,
  languagesError: null,
  languageStatusChangeLoading: false,
  languageStatusChangeError: null,
  languageStatusChangeSuccess: false,
  languagesListUpdateNeeded: false,
  languagesList: null,
  languagesListLoading: true,
  languagesListError: null,
};

const fetchLanguageStart = (state: LanguageStateType): LanguageStateType => ({
  ...state,
  languageLoading: true,
  languageSuccess: false,
});

const fetchLanguageSuccess = (
  state: LanguageStateType,
  action: LanguageActionType,
): LanguageStateType => ({
  ...state,
  language: action.language,
  languageSuccess: true,
  languageLoading: false,
  languageError: null,
});

const fetchLanguageFail = (
  state: LanguageStateType,
  action: LanguageActionType,
): LanguageStateType => ({
  ...state,
  languageError: action.languageError,
  languageLoading: false,
});

const fetchLanguagesStart = (state: LanguageStateType): LanguageStateType => ({
  ...state,
  languagesLoading: true,
});

const fetchLanguagesSuccess = (
  state: LanguageStateType,
  action: LanguageActionType,
): LanguageStateType => ({
  ...state,
  languages: action.languages,
  languagesLoading: false,
  languagesError: null,
});

const fetchLanguagesFail = (
  state: LanguageStateType,
  action: LanguageActionType,
): LanguageStateType => ({
  ...state,
  languagesError: action.languagesError,
  languagesLoading: false,
});

const changeLanguageStatusStart = (
  state: LanguageStateType,
): LanguageStateType => ({
  ...state,
  languageStatusChangeLoading: true,
});

const changeLanguageStatusSuccess = (
  state: LanguageStateType,
): LanguageStateType => ({
  ...state,
  languageStatusChangeLoading: false,
  languageStatusChangeError: null,
  languageStatusChangeSuccess: true,
  languagesListUpdateNeeded: true,
});

const changeLanguageStatusFail = (
  state: LanguageStateType,
  action: LanguageActionType,
): LanguageStateType => ({
  ...state,
  languageStatusChangeLoading: false,
});

const fetchLanguagesListStart = (
  state: LanguageStateType,
): LanguageStateType => ({
  ...state,
  languagesListLoading: true,
});

const fetchLanguagesListSuccess = (
  state: LanguageStateType,
  action: LanguageActionType,
): LanguageStateType => ({
  ...state,
  languagesListLoading: false,
  languagesList: action.languagesList,
  languagesListError: null,
  languagesListUpdateNeeded: false,
});

const fetchLanguagesListFail = (
  state: LanguageStateType,
  action: LanguageActionType,
): LanguageStateType => ({
  ...state,
  languagesListLoading: false,
  languagesListError: action.languagesListError,
});

const reducer = (state = initialState, action: LanguageActionType) => {
  switch (action.type) {
    case actionTypes.FETCH_LANGUAGE_START:
      return fetchLanguageStart(state);
    case actionTypes.FETCH_LANGUAGE_SUCCESS:
      return fetchLanguageSuccess(state, action);
    case actionTypes.FETCH_LANGUAGE_FAIL:
      return fetchLanguageFail(state, action);
    case actionTypes.FETCH_LANGUAGES_START:
      return fetchLanguagesStart(state);
    case actionTypes.FETCH_LANGUAGES_SUCCESS:
      return fetchLanguagesSuccess(state, action);
    case actionTypes.FETCH_LANGUAGES_FAIL:
      return fetchLanguagesFail(state, action);
    case actionTypes.CHANGE_LANGUAGE_STATUS_START:
      return changeLanguageStatusStart(state);
    case actionTypes.CHANGE_LANGUAGE_STATUS_SUCCESS:
      return changeLanguageStatusSuccess(state);
    case actionTypes.CHANGE_LANGUAGE_STATUS_FAIL:
      return changeLanguageStatusFail(state, action);
    case actionTypes.FETCH_LANGUAGES_LIST_START:
      return fetchLanguagesListStart(state);
    case actionTypes.FETCH_LANGUAGES_LIST_SUCCESS:
      return fetchLanguagesListSuccess(state, action);
    case actionTypes.FETCH_LANGUAGES_LIST_FAIL:
      return fetchLanguagesListFail(state, action);
    default:
      return state;
  }
};

export default reducer;
